import {
  CRMChannel,
  EmailConversationModel,
  TwilioConversationModel,
  WebChatBotConversationModel,
} from "@jugl-web/rest-api";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { MultiSectionLayout } from "@jugl-web/ui-components";
import { FC, useRef } from "react";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ConversationSidePanel } from "../ConversationSidePanel";
import { ChatTopBar } from "./components/ChatTopBar";
import { InstagramChatInput } from "./components/InstagramChatInput";
import {
  InstagramChatMessageWindow,
  InstagramChatMessageWindowHandle,
} from "./components/InstagramChatMessageWindow";
import { SelectChatSection } from "./components/SelectChatSection";
import { TwilioChatInput } from "./components/TwilioChatInput";
import {
  TwilioChatMessageWindow,
  TwilioChatMessageWindowHandle,
} from "./components/TwilioChatMessageWindow";
import { EmailChatInput } from "./components/EmailChatInput";
import {
  EmailChatMessageWindow,
  EmailChatMessageWindowHandle,
} from "./components/EmailChatMessageWindow";
import { WebChatInput } from "./components/WebChatInput";
import {
  WebChatMessageWindow,
  WebChatMessageWindowHandle,
} from "./components/WebChatMessageWindow";
import { WhatsappChatInput } from "./components/WhatsappChatInput/WhatsappChatInput";
import {
  WhatsappChatMessageWindow,
  WhatsappChatMessageWindowHandle,
} from "./components/WhatsappChatMessageWindow/WhatsappChatMessageWindow";

const ClientSupportChatInner: FC<{
  selectedConversation:
    | WhatsappConversationModel
    | InstagramConversationModel
    | WebChatBotConversationModel
    | TwilioConversationModel
    | EmailConversationModel;
}> = ({ selectedConversation }) => {
  const { selectedChannel, isConversationSidePanelOpen, isContactBlocked } =
    useClientSupportPageContext();
  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const isWebChannel = selectedChannel === CRMChannel.chatbot;
  const isInstagramChannel = selectedChannel === CRMChannel.instagram;
  const isTwilioChannel = selectedChannel === CRMChannel.twilio;
  const isEmailChannel = selectedChannel === CRMChannel.email;

  const messageWindowRef = useRef<WhatsappChatMessageWindowHandle>(null);
  const instagramMessageWindowRef =
    useRef<InstagramChatMessageWindowHandle>(null);
  const webMessageWindowRef = useRef<WebChatMessageWindowHandle>(null);
  const twilioMessageWindowRef = useRef<TwilioChatMessageWindowHandle>(null);
  const emailMessageWindowRef = useRef<EmailChatMessageWindowHandle>(null);

  return (
    <>
      <MultiSectionLayout
        header={<ChatTopBar />}
        rightSide={
          isConversationSidePanelOpen ? <ConversationSidePanel /> : undefined
        }
        footer={
          !isContactBlocked ? (
            isWebChannel ? (
              <WebChatInput
                chatId={selectedConversation.conv_id}
                conversationName={selectedConversation.name}
                onMessageSent={() => {
                  setTimeout(() => {
                    webMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isTwilioChannel ? (
              <TwilioChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    twilioMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isEmailChannel ? (
              <EmailChatInput
                conversation={selectedConversation as EmailConversationModel}
                onMessageSent={() => {
                  setTimeout(() => {
                    emailMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isInstagramChannel ? (
              <InstagramChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    instagramMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : (
              <WhatsappChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    messageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            )
          ) : null
        }
      >
        {isWhatsappChannel ? (
          <WhatsappChatMessageWindow
            ref={messageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isWebChannel ? (
          <WebChatMessageWindow
            ref={webMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isTwilioChannel ? (
          <TwilioChatMessageWindow
            ref={twilioMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isEmailChannel ? (
          <EmailChatMessageWindow
            ref={emailMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : (
          <InstagramChatMessageWindow
            ref={instagramMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        )}
      </MultiSectionLayout>
    </>
  );
};

export const ClientSupportChat: FC = () => {
  const { selectedConversation } = useClientSupportPageContext();

  if (!selectedConversation) {
    return <SelectChatSection />;
  }
  return <ClientSupportChatInner selectedConversation={selectedConversation} />;
};
